import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ClipboardList, ShieldCheck, BookOpen, Clock, AlertTriangle, Star, Menu, X, Brain, BarChart3, Target, Dices, PenLine, Library, XCircle, ArrowDown } from 'lucide-react';
import { SocialProofPopup } from './SocialProofPopup';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/w1jTBLc1G1DaQyQvRi6rU8/Mockup_acompanantes_terapeuticos-Photoroom.png';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay
}} data-id="element-1">
    {children}
  </motion.div>;
const scrollToCompra = () => {
  document.getElementById('compra')?.scrollIntoView({
    behavior: 'smooth'
  });
};
const CTAButton = ({
  onClick,
  children,
  className = ''
}: {
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) => <motion.button onClick={onClick} whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className={`w-full md:w-auto bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-lg md:text-xl py-5 px-8 md:px-12 rounded-full shadow-[0_0_20px_rgba(196,136,58,0.3)] hover:shadow-[0_0_30px_rgba(196,136,58,0.5)] transition-all duration-300 flex items-center justify-center gap-3 ${className}`} data-id="element-2">
    {children}
  </motion.button>;
function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
    setMobileOpen(false);
  };
  return <nav className="sticky top-0 z-50 bg-brand-dark/95 backdrop-blur-md border-b border-brand-accent/15" data-id="element-3">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4">
        <span className="text-brand-cream font-serif font-bold text-lg tracking-wide flex items-center gap-2" data-id="element-5">
          <span className="text-brand-copper" data-id="element-6">📋</span> Sistema PRO: AT
        </span>
        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8" data-id="element-7">
          <button onClick={() => scrollTo('bonos')} className="text-brand-muted hover:text-white text-sm font-medium transition-colors" data-id="element-8">
            Bonos
          </button>
          <button onClick={() => scrollTo('testimonios')} className="text-brand-muted hover:text-white text-sm font-medium transition-colors" data-id="element-9">
            Testimonios
          </button>
          <button onClick={() => scrollTo('compra')} className="bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-sm py-2.5 px-6 rounded-full hover:shadow-[0_0_20px_rgba(196,136,58,0.4)] transition-all" data-id="element-10">
            COMPRAR AHORA
          </button>
        </div>
        {/* Mobile hamburger */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-brand-cream" data-id="element-11">
          {mobileOpen ? <X className="w-6 h-6" data-id="element-12" /> : <Menu className="w-6 h-6" data-id="element-13" />}
        </button>
      </div>
      {/* Mobile menu */}
      <AnimatePresence data-id="element-14">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-brand-dark border-t border-brand-accent/15 overflow-hidden" data-id="element-15">
            <div className="px-4 py-4 flex flex-col gap-3" data-id="element-16">
              <button onClick={() => scrollTo('bonos')} className="text-brand-muted hover:text-white text-left py-2 text-base font-medium transition-colors" data-id="element-17">
                Bonos
              </button>
              <button onClick={() => scrollTo('testimonios')} className="text-brand-muted hover:text-white text-left py-2 text-base font-medium transition-colors" data-id="element-18">
                Testimonios
              </button>
              <button onClick={() => scrollTo('compra')} className="bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-base py-3 px-6 rounded-full text-center mt-2" data-id="element-19">
                COMPRAR AHORA
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}
export function LandingPage() {
  return <div className="min-h-screen bg-brand-cream overflow-hidden selection:bg-brand-copper selection:text-white" data-id="element-20">
      <SocialProofPopup data-id="element-21" />
      <Navbar data-id="element-22" />

      {/* HERO SECTION */}
      <section className="relative pt-10 pb-16 md:pt-16 md:pb-24 px-4" data-id="element-23">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_center,rgba(168,196,171,0.2)_0%,rgba(248,245,240,0)_100%)] pointer-events-none" data-id="element-24" />

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-25">
          <FadeIn data-id="element-26">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-brand-dark leading-tight mb-10" data-id="element-27">
              Sistema PRO:{' '}
              <span className="text-gradient" data-id="element-28">Acompañantes Terapéuticos</span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-29">
            <img src={PRODUCT_IMAGE} alt="Sistema PRO Acompañante Terapéutico" className="w-full max-w-2xl mx-auto cursor-pointer hover:scale-[1.02] transition-transform duration-500" onClick={scrollToCompra} data-id="element-30" />
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-31">
            <p className="text-xl md:text-2xl text-brand-muted mt-10 mb-10 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-32">
              Sistema que permite a los Acompañantes Terapéuticos crear informes
              profesionales en minutos… sin tener que escribir todo desde cero.
            </p>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-33">
            <div className="flex justify-center" data-id="element-34">
              <CTAButton onClick={scrollToCompra} className="animate-pulse-slow" data-id="element-35">
                👉 Acceder al Sistema PRO ahora
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROBLEMS → SOLUTIONS */}
      <section className="py-14 px-4" data-id="element-36">
        <div className="max-w-5xl mx-auto" data-id="element-37">
          <FadeIn data-id="element-38">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-dark text-center mb-10" data-id="element-39">
              ¿Sos acompañante terapeutico y te pasa alguna de estas
              situaciones?
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-40">
            {/* Problems */}
            <FadeIn delay={0.1} data-id="element-41">
              <div className="bg-red-50 border border-red-200/60 rounded-2xl p-6 h-full" data-id="element-42">
                <div className="space-y-3" data-id="element-43">
                  {['Pasás horas escribiendo informes después del trabajo', 'No sabés cómo redactar informes clínicos correctamente', 'Te cuesta organizar el seguimiento de tus pacientes', 'Muchas veces improvisás actividades en el acompañamiento', 'Sentís que te faltan herramientas profesionales'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-44">
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-45" />
                      <span className="text-sm text-brand-dark/80" data-id="element-46">{item}</span>
                    </div>)}
                </div>
              </div>
            </FadeIn>

            {/* Solutions */}
            <FadeIn delay={0.2} data-id="element-47">
              <div className="bg-emerald-50 border border-emerald-200/60 rounded-2xl p-6 h-full" data-id="element-48">
                <p className="text-sm font-bold text-brand-copper uppercase tracking-wider mb-4" data-id="element-49">
                  Con Sistema Pro AT vas a poder:
                </p>
                <div className="space-y-3" data-id="element-50">
                  {['Redactar informes profesionales en minutos', 'Registrar el progreso de tus pacientes de forma clara', 'Organizar todo tu trabajo terapéutico', 'Tener recursos listos para cada sesión'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-51">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-52" />
                      <span className="text-sm text-brand-dark/80 font-medium" data-id="element-53">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.3} data-id="element-54">
            <p className="text-center text-brand-copper font-semibold mt-8 text-lg" data-id="element-55">
              Sistema Pro AT fue creado para resolver exactamente estos
              problemas.
            </p>
            <div className="flex justify-center mt-2" data-id="element-56">
              <ArrowDown className="w-5 h-5 text-brand-copper animate-bounce" data-id="element-57" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* MODULES OVERVIEW */}
      <section className="py-20 px-4 bg-brand-sage border-y border-brand-accent/30" data-id="element-58">
        <div className="max-w-5xl mx-auto" data-id="element-59">
          <FadeIn data-id="element-60">
            <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-4 text-center" data-id="element-61">
              ¿Qué ofrece el{' '}
              <span className="text-gradient" data-id="element-62">Sistema Pro AT</span>?
            </h2>
            <p className="text-center text-lg text-brand-muted mb-14 max-w-2xl mx-auto" data-id="element-63">
              8 módulos diseñados para cubrir cada aspecto de tu trabajo
              profesional
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-64">
            {[{
            emoji: '🧠',
            icon: <Brain className="w-7 h-7 text-brand-copper" data-id="element-65" />,
            num: 1,
            title: 'Plantillas de Informes Profesionales',
            desc: 'Informes clínicos listos para completar: seguimiento, evolución, obra social y equipos interdisciplinarios.'
          }, {
            emoji: '📋',
            icon: <ClipboardList className="w-7 h-7 text-brand-copper" data-id="element-66" />,
            num: 2,
            title: 'Sistema de Registro Diario',
            desc: 'Registrá actividades, conductas, emociones e intervenciones de forma organizada día a día.'
          }, {
            emoji: '📊',
            icon: <BarChart3 className="w-7 h-7 text-brand-copper" data-id="element-67" />,
            num: 3,
            title: 'Seguimiento y Evolución',
            desc: 'Monitoreá el progreso del paciente con plantillas de objetivos, cambios y avances.'
          }, {
            emoji: '⚠️',
            icon: <AlertTriangle className="w-7 h-7 text-brand-copper" data-id="element-68" />,
            num: 4,
            title: 'Registro de Situaciones Críticas',
            desc: 'Documentá crisis, incidentes y conductas disruptivas de forma clara y profesional.'
          }, {
            emoji: '🎯',
            icon: <Target className="w-7 h-7 text-brand-copper" data-id="element-69" />,
            num: 5,
            title: 'Recursos de Intervención',
            desc: 'Fichas y actividades para regulación emocional, habilidades sociales y desarrollo personal.'
          }, {
            emoji: '🎲',
            icon: <Dices className="w-7 h-7 text-brand-copper" data-id="element-70" />,
            num: 6,
            title: 'Actividades para Niños y Adolescentes',
            desc: 'Dinámicas terapéuticas pensadas para pacientes jóvenes y el vínculo terapéutico.'
          }, {
            emoji: '✍️',
            icon: <PenLine className="w-7 h-7 text-brand-copper" data-id="element-71" />,
            num: 7,
            title: 'Guía de Redacción Profesional',
            desc: 'Aprendé a redactar informes claros y útiles, evitando errores comunes.'
          }, {
            emoji: '📚',
            icon: <Library className="w-7 h-7 text-brand-copper" data-id="element-72" />,
            num: 8,
            title: 'Banco de Frases Profesionales',
            desc: 'Frases clínicas listas para usar en informes, ahorrando tiempo sin perder calidad.'
          }].map((mod, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-73">
                <div className="glass-card p-5 rounded-2xl h-full flex flex-col hover:border-brand-copper/40 transition-colors duration-300 group" data-id="element-74">
                  <div className="flex items-center gap-3 mb-3" data-id="element-75">
                    <span className="text-2xl" data-id="element-76">{mod.emoji}</span>
                    <span className="text-xs font-bold text-brand-copper/70 uppercase tracking-wider" data-id="element-77">
                      Módulo {mod.num}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-brand-dark mb-2 leading-snug" data-id="element-78">
                    {mod.title}
                  </h3>
                  <p className="text-sm text-brand-muted leading-relaxed flex-grow" data-id="element-79">
                    {mod.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.7} data-id="element-80">
            <div className="mt-14 flex justify-center" data-id="element-81">
              <CTAButton onClick={scrollToCompra} data-id="element-82">
                👉 Acceder a los 8 módulos ahora
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* BONUSES */}
      <section id="bonos" className="py-14 px-4 relative" data-id="element-83">
        <div className="max-w-4xl mx-auto" data-id="element-84">
          <FadeIn data-id="element-85">
            <div className="text-center mb-8" data-id="element-86">
              <span className="bg-red-500/10 text-red-500 font-bold px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border border-red-500/20 inline-block mb-4 animate-pulse" data-id="element-87">
                🎁 GRATIS por el día de hoy
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-brand-dark" data-id="element-88">
                Bonos Exclusivos
              </h2>
            </div>

            <div className="space-y-3" data-id="element-89">
              {[{
              icon: <BookOpen className="w-6 h-6 text-brand-copper" data-id="element-90" />,
              title: 'Biblioteca de Frases Profesionales para Informes',
              desc: 'El vocabulario técnico exacto para que tus informes destaquen.'
            }, {
              icon: <Clock className="w-6 h-6 text-brand-copper" data-id="element-91" />,
              title: 'Pack de Informes Express (5 minutos)',
              desc: 'Plantillas ultra-optimizadas para cuando no tenés tiempo.'
            }, {
              icon: <ShieldCheck className="w-6 h-6 text-brand-copper" data-id="element-92" />,
              title: 'Manual de Situaciones Difíciles en AT',
              desc: 'Cómo documentar crisis y situaciones complejas correctamente.'
            }].map((bonus, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-93">
                  <div className="bg-gradient-to-r from-white to-brand-sage border border-brand-copper/25 px-5 py-4 rounded-xl flex items-center gap-4 relative overflow-hidden" data-id="element-94">
                    <div className="bg-brand-sage p-2.5 rounded-lg border border-brand-copper/20 flex-shrink-0" data-id="element-95">
                      {bonus.icon}
                    </div>
                    <div className="flex-grow min-w-0" data-id="element-96">
                      <h3 className="text-base font-bold text-brand-dark leading-snug" data-id="element-97">
                        {bonus.title}
                      </h3>
                      <p className="text-sm text-brand-muted mt-0.5" data-id="element-98">
                        {bonus.desc}
                      </p>
                    </div>
                    <span className="text-brand-copper font-bold bg-brand-copper/10 px-3 py-1.5 rounded-full text-xs flex-shrink-0 hidden md:block" data-id="element-99">
                      INCLUIDO
                    </span>
                  </div>
                </FadeIn>)}
            </div>

            <FadeIn delay={0.3} data-id="element-100">
              <div className="mt-6 text-center bg-white/60 border border-brand-copper/20 rounded-xl py-4 px-5" data-id="element-101">
                <p className="text-brand-muted text-sm" data-id="element-102">
                  Valor total de los 3 bonos:{' '}
                  <span className="line-through text-brand-muted/60 font-bold" data-id="element-103">
                    $29.990
                  </span>
                </p>
                <p className="text-brand-copper font-bold text-lg mt-1" data-id="element-104">
                  Por el día de hoy →{' '}
                  <span className="bg-brand-copper/10 px-3 py-1 rounded-full" data-id="element-105">
                    GRATIS
                  </span>
                </p>
              </div>
            </FadeIn>
          </FadeIn>
        </div>
      </section>

      {/* PRICING / CTA SECTION */}
      <section id="compra" className="py-14 px-4 bg-brand-sage border-t border-brand-copper/20 relative overflow-hidden" data-id="element-106">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-brand-copper/8 rounded-full blur-3xl pointer-events-none" data-id="element-107" />

        <div className="max-w-xs mx-auto relative z-10" data-id="element-108">
          <FadeIn data-id="element-109">
            <div className="glass-card rounded-2xl relative overflow-hidden" data-id="element-110">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#c4883a] to-[#d4943f]" data-id="element-111" />

              {/* Top info area */}
              <div className="px-5 pt-5 pb-3 text-center" data-id="element-112">
                <span className="bg-brand-copper/15 text-brand-copper font-bold text-[11px] uppercase tracking-widest px-3 py-1 rounded-full inline-block mb-3" data-id="element-113">
                  Oferta Limitada
                </span>

                <h2 className="text-lg font-bold text-brand-dark mb-0.5" data-id="element-114">
                  Sistema PRO: Acompañante Terapéutico
                </h2>
                <p className="text-xs text-brand-muted" data-id="element-115">
                  Todo lo que necesitás para tu trabajo profesional
                </p>
              </div>

              {/* Includes list */}
              <div className="border-t border-brand-accent/30 px-5 py-3" data-id="element-116">
                <p className="text-[11px] font-bold text-brand-dark uppercase tracking-wider mb-2" data-id="element-117">
                  Incluye:
                </p>
                <div className="space-y-1" data-id="element-118">
                  {['8 módulos profesionales completos', 'Plantillas de informes listas para usar', 'Sistema de registro diario', 'Recursos de intervención y actividades', 'Banco de frases clínicas', '3 bonos exclusivos gratis'].map((item, i) => <div key={i} className="flex items-center gap-2" data-id="element-119">
                      <CheckCircle2 className="w-3 h-3 text-brand-copper flex-shrink-0" data-id="element-120" />
                      <span className="text-xs text-brand-dark/80" data-id="element-121">{item}</span>
                    </div>)}
                </div>
              </div>

              {/* Pricing + CTA */}
              <div className="border-t border-brand-accent/30 px-5 pt-3 pb-5 text-center" data-id="element-122">
                <p className="text-sm text-brand-muted/60 line-through font-bold" data-id="element-123">
                  $59.990
                </p>
                <div className="flex items-center justify-center gap-2 mt-0.5 mb-3" data-id="element-124">
                  <p className="text-3xl font-bold text-brand-dark tracking-tight" data-id="element-125">
                    $19.990
                  </p>
                  <span className="bg-brand-copper text-white text-[10px] font-bold px-2 py-0.5 rounded-md" data-id="element-126">
                    -67%
                  </span>
                </div>

                <a href="Link de tu checkout" className="block w-full bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-base py-3 px-5 rounded-full shadow-[0_0_25px_rgba(196,136,58,0.35)] hover:shadow-[0_0_35px_rgba(196,136,58,0.55)] transform hover:scale-[1.03] transition-all duration-300 animate-pulse-slow" data-id="element-127">
                  👉 Sí, quiero el Sistema PRO
                </a>

                <p className="text-[11px] text-brand-muted mt-2" data-id="element-128">
                  Pago único · Acceso de por vida
                </p>

                <div className="mt-3 pt-3 border-t border-brand-accent/20 flex flex-col items-center" data-id="element-129">
                  <div className="flex items-center gap-1.5" data-id="element-130">
                    <ShieldCheck className="w-3 h-3 text-brand-copper" data-id="element-131" />
                    <span className="text-[11px] text-brand-muted font-medium" data-id="element-132">
                      Compra segura - Garantía de satisfacción
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonios" className="py-24 px-4 bg-brand-sage border-y border-brand-accent/30" data-id="element-133">
        <div className="max-w-5xl mx-auto" data-id="element-134">
          <FadeIn data-id="element-135">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-16 text-center" data-id="element-136">
              Lo que dicen otros profesionales
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-137">
              {[{
              name: 'Luciana',
              age: 28,
              city: 'Buenos Aires',
              text: 'Antes pasaba horas el fin de semana armando informes. Con estas plantillas, en 15 minutos tengo todo listo y súper profesional. Me salvó la vida.'
            }, {
              name: 'Martín',
              age: 35,
              city: 'Córdoba',
              text: 'Siempre sentí que mis registros no estaban a la altura de los psicólogos del equipo. Ahora entrego informes que dan gusto leer. Me dio muchísima seguridad.'
            }, {
              name: 'Valeria',
              age: 42,
              city: 'Rosario',
              text: 'Trabajo con tres obras sociales distintas y cada una pide cosas diferentes. El pack me organizó la cabeza y el trabajo. Excelente inversión.'
            }, {
              name: 'Federico',
              age: 30,
              city: 'Mendoza',
              text: 'Llegaba a casa agotado y tener que escribir era una tortura. El sistema de frases profesionales es oro puro. Lo recomiendo a todos los colegas.'
            }, {
              name: 'Camila',
              age: 26,
              city: 'La Plata',
              text: 'Recién empiezo en la profesión y estaba perdidísima con la parte administrativa. Esto es literalmente lo que no te enseñan en el curso de AT.'
            }].map((testimonial, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-138">
                  <div className="glass-card p-6 rounded-2xl h-full flex flex-col" data-id="element-139">
                    <div className="flex gap-1 mb-4" data-id="element-140">
                      {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-brand-copper text-brand-copper" data-id="element-141" />)}
                    </div>
                    <p className="text-brand-dark italic flex-grow mb-6" data-id="element-142">
                      "{testimonial.text}"
                    </p>
                    <div className="mt-auto border-t border-brand-accent/30 pt-4" data-id="element-143">
                      <p className="font-bold text-brand-dark" data-id="element-144">
                        {testimonial.name}, {testimonial.age} años
                      </p>
                      <p className="text-sm text-brand-muted" data-id="element-145">
                        {testimonial.city}, Argentina
                      </p>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="relative py-20 px-4 overflow-hidden" data-id="element-146">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark via-brand-forest to-brand-dark" data-id="element-147" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(45,74,53,0.8)_0%,rgba(26,46,31,1)_70%)]" data-id="element-148" />

        <div className="max-w-2xl mx-auto relative z-10 text-center" data-id="element-149">
          <FadeIn data-id="element-150">
            <h2 className="font-serif italic text-3xl md:text-5xl font-bold text-white mb-10 leading-tight" data-id="element-151">
              Hacé clic y empezá hoy.
            </h2>

            <motion.a href="Link de tu checkout" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-3 bg-white text-brand-dark font-bold text-lg md:text-xl uppercase tracking-wider py-5 px-10 md:px-14 rounded-full shadow-[0_0_40px_rgba(255,255,255,0.2)] hover:shadow-[0_0_60px_rgba(255,255,255,0.35)] transition-shadow duration-500" data-id="element-152">
              Acceder ahora
              <span className="text-xl" data-id="element-153">→</span>
            </motion.a>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 text-center border-t border-brand-accent/30 text-brand-muted text-sm" data-id="element-154">
        <p data-id="element-155">
          © {new Date().getFullYear()} Sistema PRO: Acompañante Terapéutico.
          Todos los derechos reservados.
        </p>
        
      </footer>
    </div>;
}